/**
 * ============================================================================
 * BOOKING FILTERS - SINGLE SOURCE OF TRUTH
 * ============================================================================
 *
 * ⚠️  CRITICAL: This is the ONLY place where filtering logic lives!
 *
 * RULES:
 * 1. applyAllFilters() is the SINGLE entry point for filtering
 * 2. calculateTabCounts() MUST use the same logic as applyTabFilter()
 * 3. NEVER write filter logic directly in components
 * 4. Tab counts MUST ALWAYS match the actual filtered data
 *
 * FILTER ORDER (DO NOT CHANGE):
 * 1. Tab filter (today/upcoming/recurring/cancelled/all)
 * 2. Date range filter (7/30/90/all days)
 * 3. Status filter (confirmed/completed/etc)
 * 4. Sport filter (basketball/football/etc)
 * 5. Court filter (court 1/court 2/etc)
 * 6. Search box (applied by TanStack Table)
 *
 * DATE FILTERING:
 * - Filters by SESSION date (when booking happens)
 * - NOT by payment date or creation date
 * - For recurring: uses displayed session date (next or last)
 *
 * TAB COUNTS:
 * - If tab count doesn't match filtered data, you broke something
 * - The count logic is in calculateTabCounts()
 * - It uses applyTabFilter() to ensure consistency
 *
 * ============================================================================
 */

import { format } from 'date-fns';
import type { BookingRow, TabType, FilterState } from '../types/bookings';

/**
 * Applies tab filter to bookings
 */
export function applyTabFilter(data: BookingRow[], activeTab: TabType): BookingRow[] {
  const today = format(new Date(), 'yyyy-MM-dd');

  // Exclude regular blocked slots from ALL booking tabs (they only appear in calendar)
  // But INCLUDE external (partner) blocks - active ones show in calendar, cancelled ones in Cancelled tab
  const bookingsOnly = data.filter((row) => (row.type as any) !== 'blocked');

  switch (activeTab) {
    case 'today':
      return bookingsOnly.filter((row) => row.date === today);

    case 'upcoming':
      return bookingsOnly.filter(
        (row) =>
          row.date > today &&
          row.status !== 'cancelled' &&
          row.status !== 'completed' &&
          row.paymentStatus !== 'refunded' && // Exclude refunded from upcoming
          // Exclude non-trusted pending bookings (drafts), but keep trusted reservations
          (row.paymentStatus !== 'pending' || (row.rawData as any)?.is_trusted_reservation === true)
      );

    case 'recurring':
      // Include both SPOTTS recurring and manual recurring bookings
      return bookingsOnly.filter((row) => row.type === 'recurring' || row.type === 'manual_recurring');

    case 'cancelled':
      return bookingsOnly.filter(
        (row) =>
          row.status === 'cancelled' ||
          row.paymentStatus === 'refunded'
      );

    case 'all':
    default:
      return bookingsOnly; // All bookings, but NOT blocked slots
  }
}

/**
 * Applies date range filter to bookings
 */
export function applyDateRangeFilter(
  data: BookingRow[],
  dateRange: FilterState['dateRange']
): BookingRow[] {
  if (dateRange === 'all') {
    return data;
  }

  const today = new Date();
  const days = parseInt(dateRange);
  const startDate = new Date(today);
  startDate.setDate(startDate.getDate() - days);
  const endDate = new Date(today);
  endDate.setDate(endDate.getDate() + days);

  const startStr = format(startDate, 'yyyy-MM-dd');
  const endStr = format(endDate, 'yyyy-MM-dd');

  return data.filter((row) => {
    if (!row.date || row.date === 'N/A') return false;
    return row.date >= startStr && row.date <= endStr;
  });
}

/**
 * Applies all filters to bookings (single source of truth)
 */
export function applyAllFilters(
  data: BookingRow[],
  activeTab: TabType,
  filters: FilterState
): BookingRow[] {
  let filtered = data;

  // 1. Apply tab filter
  filtered = applyTabFilter(filtered, activeTab);

  // 2. Apply date range filter
  filtered = applyDateRangeFilter(filtered, filters.dateRange);

  // 3. Apply status filter
  if (filters.status !== 'all') {
    filtered = filtered.filter((row) => row.status === filters.status);
  }

  // 4. Apply sport filter
  if (filters.sport !== 'all') {
    filtered = filtered.filter((row) => row.sport === filters.sport);
  }

  // 5. Apply court filter
  if (filters.court !== 'all') {
    filtered = filtered.filter((row) => row.court === filters.court);
  }

  // 6. Apply source filter (Manual/Spotts/All)
  if (filters.source !== 'all') {
    filtered = filtered.filter((row) => {
      // Manual recurring groups are always 'manual' source
      if (row.type === 'manual_recurring') {
        return filters.source === 'manual';
      }
      const rawData = row.rawData as any;
      const source = rawData?.source || 'spotts'; // Default to 'spotts' for app bookings
      return source === filters.source;
    });
  }

  return filtered;
}

/**
 * Calculate tab counts (must use same logic as applyTabFilter)
 */
export function calculateTabCounts(data: BookingRow[]): Record<TabType, number> {
  return {
    today: applyTabFilter(data, 'today').length,
    upcoming: applyTabFilter(data, 'upcoming').length,
    recurring: applyTabFilter(data, 'recurring').length,
    cancelled: applyTabFilter(data, 'cancelled').length,
    all: applyTabFilter(data, 'all').length,
  };
}

/**
 * Get unique values for filter dropdowns
 */
export function getUniqueValues(data: BookingRow[], key: 'sport' | 'court' | 'status'): string[] {
  const values = new Set(
    data
      .map((row) => row[key])
      .filter((value) => value && value !== 'N/A')
  );
  return ['all', ...Array.from(values).sort()];
}

/**
 * Check if any filters are active (for showing "Clear Filters" button)
 */
export function hasActiveFilters(filters: FilterState): boolean {
  return (
    filters.dateRange !== '90' ||
    filters.sport !== 'all' ||
    filters.court !== 'all' ||
    filters.status !== 'all' ||
    filters.source !== 'all'
  );
}

/**
 * Get initial/default filter state
 */
export function getDefaultFilters(): FilterState {
  return {
    dateRange: '365',
    sport: 'all',
    court: 'all',
    status: 'all',
    source: 'all',
  };
}

/**
 * ============================================================================
 * CALENDAR VIEW HELPERS
 * ============================================================================
 */

/**
 * Groups bookings by date for calendar view
 * Bookings are sorted by start time (earliest first)
 */
export function groupBookingsByDate(bookings: BookingRow[]): Record<string, BookingRow[]> {
  const grouped: Record<string, BookingRow[]> = {};

  bookings.forEach((booking) => {
    if (!booking.date || booking.date === 'N/A') return;

    if (!grouped[booking.date]) {
      grouped[booking.date] = [];
    }
    grouped[booking.date].push(booking);
  });

  // Sort each day's bookings by start time (earliest first)
  Object.keys(grouped).forEach((date) => {
    grouped[date].sort((a, b) => {
      // Extract start time from "HH:MM - HH:MM" format
      const aTime = a.time?.split(' - ')[0] || '00:00';
      const bTime = b.time?.split(' - ')[0] || '00:00';
      return aTime.localeCompare(bTime);
    });
  });

  return grouped;
}

/**
 * Gets all days in a month (for calendar grid)
 */
export function getMonthDays(date: Date): Date[] {
  const year = date.getFullYear();
  const month = date.getMonth();

  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);

  const days: Date[] = [];

  // Add previous month days to fill the grid
  const firstDayOfWeek = firstDay.getDay();
  const daysToAdd = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1; // Monday = 0

  for (let i = daysToAdd; i > 0; i--) {
    const day = new Date(year, month, 1 - i);
    days.push(day);
  }

  // Add current month days
  for (let i = 1; i <= lastDay.getDate(); i++) {
    days.push(new Date(year, month, i));
  }

  // Add next month days to complete the grid (42 cells = 6 rows)
  const remainingDays = 42 - days.length;
  for (let i = 1; i <= remainingDays; i++) {
    days.push(new Date(year, month + 1, i));
  }

  return days;
}

/**
 * Gets days for week view
 */
export function getWeekDays(date: Date): Date[] {
  const dayOfWeek = date.getDay();
  const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Monday = 0

  const monday = new Date(date);
  monday.setDate(date.getDate() - diff);

  const days: Date[] = [];
  for (let i = 0; i < 7; i++) {
    const day = new Date(monday);
    day.setDate(monday.getDate() + i);
    days.push(day);
  }

  return days;
}

/**
 * Checks if a date is today
 */
export function isToday(date: Date): boolean {
  const today = new Date();
  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  );
}

/**
 * Checks if a date is in the current month
 */
export function isCurrentMonth(date: Date, currentMonth: Date): boolean {
  return (
    date.getMonth() === currentMonth.getMonth() &&
    date.getFullYear() === currentMonth.getFullYear()
  );
}

/**
 * Gets booking type color for calendar
 * Handles: cancelled/refunded (red), manual bookings (green), blocked slots (orange), external/partner (teal), recurring (purple), manual_recurring (green+purple), spotts (blue)
 */
export function getBookingTypeColor(type: 'onetime' | 'recurring' | 'blocked' | 'manual_recurring' | 'external', booking?: any): string {
  // Check if booking is cancelled OR refunded
  if (booking?.status === 'cancelled' || booking?.payment_status === 'refunded') {
    return 'bg-red-50 border-red-400 text-red-700 opacity-75 line-through';
  }

  // Check if it's an external/partner reservation
  if (type === 'external') {
    return 'bg-teal-100 border-teal-400 text-teal-800';
  }

  // Check if it's a blocked slot
  if (type === 'blocked') {
    return 'bg-orange-100 border-orange-400 text-orange-800';
  }

  // Manual recurring bookings (green with purple accent)
  if (type === 'manual_recurring') {
    return 'bg-green-100 border-green-400 text-green-800';
  }

  // Check if booking is manual (only for one-time bookings)
  if (type === 'onetime' && booking?.source === 'manual') {
    return 'bg-green-100 border-green-400 text-green-800';
  }

  // Regular booking types
  switch (type) {
    case 'recurring':
      return 'bg-purple-100 border-purple-400 text-purple-800'; // Recurring = Purple
    case 'onetime':
    default:
      return 'bg-blue-100 border-blue-400 text-blue-800'; // Spotts = Blue
  }
}

/**
 * ============================================================================
 * OPERATING HOURS & BLACKOUT DATES HELPERS
 * ============================================================================
 */

/**
 * Day of week mapping for operating hours
 */
const dayOfWeekMap: Record<number, string> = {
  0: 'sunday',
  1: 'monday',
  2: 'tuesday',
  3: 'wednesday',
  4: 'thursday',
  5: 'friday',
  6: 'saturday',
};

/**
 * Checks if a date falls on a closed day based on operating hours
 */
export function isClosedDay(
  date: Date,
  operatingHours?: Array<{ day_of_week: string; is_closed: boolean }>
): boolean {
  if (!operatingHours || operatingHours.length === 0) {
    return false;
  }

  const dayName = dayOfWeekMap[date.getDay()];
  const dayHours = operatingHours.find(
    (oh) => oh.day_of_week.toLowerCase() === dayName
  );

  return dayHours?.is_closed ?? false;
}

/**
 * Checks if a date is a blackout date
 */
export function isBlackoutDate(
  date: Date,
  blackoutDates?: Array<{ date: string; reason: string }>
): { isBlackout: boolean; reason?: string } {
  if (!blackoutDates || blackoutDates.length === 0) {
    return { isBlackout: false };
  }

  const dateStr = format(date, 'yyyy-MM-dd');
  const blackout = blackoutDates.find((bd) => bd.date === dateStr);

  if (blackout) {
    return { isBlackout: true, reason: blackout.reason };
  }

  return { isBlackout: false };
}

/**
 * ============================================================================
 * BLOCKED SLOTS CONSOLIDATION HELPERS
 * ============================================================================
 */

/**
 * Consolidates consecutive blocked slots for the same court into ranges
 * Returns both consolidated blocked slots and a summary
 */
export function consolidateBlockedSlots(bookings: BookingRow[]): {
  consolidated: BookingRow[];
  blockedSlotsCount: number;
  hasFullDayBlock: boolean;
} {
  const blocked = bookings.filter((b) => (b.type as any) === 'blocked');
  const nonBlocked = bookings.filter((b) => (b.type as any) !== 'blocked');

  if (blocked.length === 0) {
    return {
      consolidated: nonBlocked,
      blockedSlotsCount: 0,
      hasFullDayBlock: false,
    };
  }

  // Group blocked slots by court
  const blockedByCourt: Record<string, BookingRow[]> = {};
  blocked.forEach((slot) => {
    const court = slot.court || 'Unknown';
    if (!blockedByCourt[court]) {
      blockedByCourt[court] = [];
    }
    blockedByCourt[court].push(slot);
  });

  // Check if any blocked slot is long (8+ hours = likely full day)
  const hasFullDayBlock = blocked.some((slot) => {
    const [startTime, endTime] = slot.time.split(' - ');
    if (!startTime || !endTime) return false;

    const startMinutes = timeToMinutes(startTime);
    const endMinutes = timeToMinutes(endTime);
    const durationHours = (endMinutes - startMinutes) / 60;

    return durationHours >= 8;
  });

  // Consolidate consecutive slots per court
  const consolidatedBlocked: BookingRow[] = [];

  Object.entries(blockedByCourt).forEach(([court, slots]) => {
    // Sort by start time
    const sorted = [...slots].sort((a, b) => {
      const aTime = a.time?.split(' - ')[0] || '00:00';
      const bTime = b.time?.split(' - ')[0] || '00:00';
      return aTime.localeCompare(bTime);
    });

    if (sorted.length <= 2) {
      // If only 1-2 slots, show them individually
      consolidatedBlocked.push(...sorted);
    } else {
      // Multiple slots - consolidate into a summary entry
      const firstStart = sorted[0].time?.split(' - ')[0] || '';
      const lastEnd = sorted[sorted.length - 1].time?.split(' - ')[1] || '';

      // Create a consolidated entry
      consolidatedBlocked.push({
        ...sorted[0],
        customer: `${sorted.length} Blocked Slots`,
        time: `${firstStart} - ${lastEnd}`,
        reference: `BLOCKED-MULTI-${court}`,
      });
    }
  });

  return {
    consolidated: [...nonBlocked, ...consolidatedBlocked],
    blockedSlotsCount: blocked.length,
    hasFullDayBlock,
  };
}

/**
 * Convert time string (HH:MM or HH:MM:SS) to minutes from midnight
 */
function timeToMinutes(time: string): number {
  const parts = time.split(':');
  const hours = parseInt(parts[0], 10) || 0;
  const minutes = parseInt(parts[1], 10) || 0;
  return hours * 60 + minutes;
}
